import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Crown, Check, Sparkles, Eye, MessageCircle, UserPlus, Loader2, CheckCircle2, XCircle,
  Heart, Star, Undo2, Globe, Rocket, Send,
} from "lucide-react";

export default function Vip() {
  const { t } = useTranslation();
  // Complete list of everything VIP unlocks across the app.
  const PERK_ICONS = [
    { icon: Heart, label: t("vipPage.perkSwipes") },
    { icon: Star, label: t("vipPage.perkSuperLike") },
    { icon: Undo2, label: t("vipPage.perkUndo") },
    { icon: Globe, label: t("vipPage.perkPassport") },
    { icon: Rocket, label: t("vipPage.perkBoost") },
    { icon: Send, label: t("vipPage.perkFreeDm") },
    { icon: MessageCircle, label: t("vip.chatAnyone") },
    { icon: Eye, label: t("vip.seeProfileViews") },
    { icon: UserPlus, label: t("profile.addFriend") },
    { icon: Sparkles, label: t("profile.vibeMatches") },
  ];
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [polling, setPolling] = useState(null);
  const [activatingPerma, setActivatingPerma] = useState(false);

  const isMaster = user?.is_master || user?.email === "mustafahaidari03@gmail.com";

  const activatePermaVip = async () => {
    setActivatingPerma(true);
    try {
      await api.post("/users/me/vip");
      await refresh();
      toast.success("Permanent VIP activated successfully! 👑");
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
    } finally {
      setActivatingPerma(false);
    }
  };

  useEffect(() => {
    api.get("/vip/plans").then((r) => setPlans(r.data.plans));
  }, []);

  // Detect return from Stripe and poll status
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let cancelled = false;
    let attempt = 0;
    setPolling({ status: "checking" });
    const poll = async () => {
      if (cancelled) return;
      attempt += 1;
      try {
        const { data: tx } = await api.get(`/payments/status/${sid}`);
        if (tx.payment_status === "paid") {
          setPolling({ status: "paid", tx });
          toast.success(t("vipPage.alreadyVip") + " 👑");
          await refresh();
          setTimeout(() => setParams({}), 1500);
          return;
        }
        if (tx.payment_status === "expired" || tx.status === "expired") {
          setPolling({ status: "expired" }); return;
        }
        if (attempt >= 10) { setPolling({ status: "error" }); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempt >= 10) { setPolling({ status: "error" }); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, [params.get("session_id")]);

  const subscribe = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/payments/checkout/vip", {
        plan: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
      setBusy(null);
    }
  };

  const isVip = user?.is_vip;
  const vipUntil = user?.vip_until ? new Date(user.vip_until) : null;
  const daysLeft = vipUntil ? Math.max(0, Math.ceil((vipUntil - new Date()) / (1000*60*60*24))) : 0;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-32">
      <button onClick={() => nav(-1)} className="btn-nb mb-5" data-testid="back-btn"><ArrowLeft className="w-4 h-4" strokeWidth={2.5}/> {t("common.back")}</button>

      {polling && polling.status !== "paid" && (
        <div className={`card-nb p-4 mb-5 flex items-center gap-3 ${polling.status === "error" || polling.status === "expired" ? "bg-[#FFE6E0]" : "bg-[#FFF1D6]"}`} data-testid="vip-polling">
          {polling.status === "checking" && <><Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5}/> <span>{t("coinsPage.confirming")}</span></>}
          {polling.status === "expired" && <><XCircle className="w-5 h-5" strokeWidth={2.5}/> <span>{t("coinsPage.expired")}</span></>}
          {polling.status === "error" && <><XCircle className="w-5 h-5" strokeWidth={2.5}/> <span>{t("coinsPage.failed")}</span></>}
        </div>
      )}
      {polling?.status === "paid" && (
        <div className="card-nb p-4 mb-5 flex items-center gap-3 bg-[#DDF5EA]" data-testid="vip-paid">
          <CheckCircle2 className="w-5 h-5 text-[#0F9D8F]" strokeWidth={2.5}/>
          <span className="font-display font-semibold">{t("coinsPage.confirmed")}</span>
        </div>
      )}

      {isMaster && (
        <div className="card-nb p-6 mb-6 bg-gradient-to-br from-[#ECE6FF] to-[#D9CFFF] border-2 border-[#121212] relative overflow-hidden" data-testid="perma-vip-section">
          <div className="absolute right-4 top-4 opacity-10">
            <Crown className="w-20 h-20 text-black" strokeWidth={1.5} fill="black" />
          </div>
          <h2 className="font-display font-black text-xl mb-1 text-[#0A0B0D]">👑 Master Admin Perma VIP Section</h2>
          <p className="text-sm text-[#4A4F58] mb-4">Activate permanent VIP status for your own account instantly without payment.</p>
          <button
            onClick={activatePermaVip}
            disabled={activatingPerma}
            className="btn-nb btn-nb-primary font-bold text-sm"
          >
            {activatingPerma ? <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5}/> Activating...</> : "Activate Permanent VIP"}
          </button>
        </div>
      )}

      <div className="card-nb p-7 mb-6 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1F2228 0%, #0F1115 50%, #F9C349 200%)" }}>
        <div className="absolute -right-10 -bottom-10 w-60 h-60 rounded-full bg-[#F9C349]/40 blur-3xl"/>
        <Crown className="w-12 h-12 text-[#F9C349] mb-3" strokeWidth={2.5} fill="#F9C349"/>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">{t("vipPage.title")}</h1>
        <p className="text-white/80 mt-2 max-w-md">{t("vipPage.subtitle")}</p>
        {isVip && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur border border-white/20" data-testid="vip-active-banner">
            <Crown className="w-4 h-4 text-[#F9C349]" strokeWidth={2.5} fill="#F9C349"/>
            <span className="font-display font-semibold">{t("profile.vipMember")} · {daysLeft}d</span>
          </div>
        )}
      </div>

      <div className="card-nb p-6 mb-6 bg-white">
        <h2 className="font-display font-extrabold text-xl mb-4 tracking-tight">{t("vipPage.whatYouGet")}</h2>
        <ul className="space-y-3">
          {PERK_ICONS.map(({ icon: Icon, label }) => (
            <li key={label} className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FFE08A] to-[#F9C349] flex items-center justify-center shrink-0 ring-1 ring-black/5">
                <Icon className="w-4 h-4 text-[#4A3000]" strokeWidth={2.5}/>
              </div>
              <span className="text-sm sm:text-base font-medium">{label}</span>
            </li>
          ))}
        </ul>
      </div>

      <h2 className="font-display font-extrabold text-2xl mb-3 tracking-tight">{t("vipPage.pickPlan")}</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((p) => {
          const monthly = (p.price_eur / (p.days/30)).toFixed(2);
          const featured = p.id === "6m";
          return (
            <div
              key={p.id}
              className={`card-nb p-6 relative ${featured ? "shadow-soft-lg" : ""}`}
              style={{ background: featured ? "linear-gradient(135deg, #FFF1D6, #FFE08A)" : "white" }}
              data-testid={`plan-${p.id}`}
            >
              {featured && <span className="absolute -top-3 left-6 tag-nb bg-gradient-to-r from-[#0F9D8F] to-[#14C8B0] text-white border-transparent">{t("vipPage.bestValue").toUpperCase()}</span>}
              <div className="font-display font-extrabold text-2xl tracking-tight">{p.label}</div>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="font-display font-black text-5xl tracking-tight">€{p.price_eur}</span>
              </div>
              <div className="text-xs text-[#4A4F58] mt-1">~${monthly}/{t("vipPage.month")}</div>
              <ul className="mt-4 space-y-1.5 text-sm">
                <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#0F9D8F]" strokeWidth={2.5}/> {t("vipPage.perkUnlimited")}</li>
                <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#0F9D8F]" strokeWidth={2.5}/> {t("vipPage.perkProfile")}</li>
                <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#0F9D8F]" strokeWidth={2.5}/> {t("vipPage.perkMatch")}</li>
              </ul>
              <button
                onClick={() => subscribe(p.id)}
                disabled={busy === p.id}
                className={`btn-nb w-full mt-5 ${featured ? "btn-nb-dark" : "btn-nb-primary"}`}
                data-testid={`subscribe-${p.id}`}
              >
                {busy === p.id ? <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5}/> {t("coinsPage.redirecting")}</> : `${t("profile.goVip")} · $€{p.price_eur}`}
              </button>
            </div>
          );
        })}
      </div>

      <div className="card-nb p-4 mt-6 bg-[#FFE6E0] border-2 border-[#FF7A6B] text-center shadow-soft" data-testid="vip-no-refund-notice">
        <div className="font-display font-extrabold text-sm text-[#D14848] flex items-center justify-center gap-1.5 uppercase tracking-wider">
          <span>⚠️</span> Strict No-Refund Policy
        </div>
        <p className="text-xs text-[#3D424B] mt-1 font-semibold leading-relaxed">
          All VIP subscription purchases are 100% final and non-refundable. Deleting your account or deactivating your profile does not entitle you to any refund. Active VIP status and benefits are permanently lost upon account deletion.
        </p>
      </div>

      <p className="text-[11px] text-[#8A8F98] mt-6 text-center">
        {t("coinsPage.testMode")}
      </p>
    </div>
  );
}
